import { Image, classNames } from "@plinknz/tah-website-elements";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { getMedia } from "../service/api-media";

export interface HeroProps {
  title?: string;
  subtitle?: string | JSX.Element;
  images?: Image[];
  size?: "large" | "normal";
  hasChildPages?: boolean;
  id?: string;
}

export const Hero = ({
  title = "",
  subtitle = "",
  images,
  hasChildPages,
  size = "normal",
}: HeroProps) => {
  const defaultImage = "/images/defaults/image1.jpg";
  const defaultVTT = "/videos/heroVideo.vtt";

  const [imageUrl, setImageUrl] = useState<string>(
    (images?.length && images[0].formats?.thumbnail.url) || defaultImage
  );
  const [videos, setVideos] = useState<
    { url: string; type: string; width: number }[]
  >([]);
  const heroElement = useRef<HTMLDivElement>();

  useEffect(() => {
    if (heroElement.current && images) {
      const currentVideos = [];
      const minSize = 600;
      const targetSize = Math.max(heroElement.current.clientWidth, minSize);

      for (const { url, formats } of images) {
        if (url) {
          if (url.match(/\.(webm|mp4|ogv)$/i)) {
            const video = { url, type: "", width: targetSize };
            if (url.match(/\.webm$/i)) {
              video.type = "video/webm";
            } else if (url.match(/\.mp4$/i)) {
              video.type = "video/mp4";
            } else if (url.match(/\.ogv$/i)) {
              video.type = "video/ogg";
            }

            currentVideos.push(video);
          } else if (formats) {
            const sizedImageUrl = getMedia(url, { formats, targetSize });
            setImageUrl(sizedImageUrl);
          }
        }
      }

      setVideos(currentVideos);
    }
  }, [heroElement.current, images]);

  const heroClass = classNames("hero", {
    "has-image": !!imageUrl,
    "has-menu": hasChildPages,
    large: size === "large",
  });
  const backgroundImage = `url('${imageUrl}')`;

  return (
    <div className={heroClass} ref={heroElement} data-testid="hero">
      <Helmet>
        {title && <meta property="og:title" content={title} />}
        {imageUrl && <meta property="og:image" content={imageUrl} />}
      </Helmet>

      {!videos.length && (
        <div
          className="hero-background"
          data-testid="hero-background"
          style={{ backgroundImage }}
        />
      )}

      {videos.length && (
        <video width={videos[0].width} autoPlay playsInline muted>
          <track src={defaultVTT} kind="captions" />
          {videos.map(({ url, type }) => (
            <source
              key={url}
              src={url}
              type={type}
              data-testid="video-source"
            />
          ))}
          <img src={imageUrl} alt="Heading" />
        </video>
      )}
      <div className="hero-content">
        <div className="title">
          <h1 className="hero-heading || heading-1">{title}</h1>
        </div>

        <div className="subtitle">
          {subtitle && <h4 className="hero-subheading">{subtitle}</h4>}
        </div>
      </div>
    </div>
  );
};
